import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import { useState, FC } from 'react';
import TextField from 'src/components/InputFields/TextField';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { fetchEnums, fetchProductUnits } from 'src/services/enums.service';
import { useFormik } from 'formik';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { productInitialValues } from 'src/utils/initialValues.utils';
import { apiUrls } from 'src/utils/properties.utils';
import { productValidation } from 'src/utils/validationScheme.utils';
import ProductGrid from 'src/components/Grids/ProductGrid';
import UnitField from 'src/components/InputFields/UnitPicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import SearchBar from 'src/components/InputFields/Searchbar';
import {
  PrimaryActionButton,
  ButtonIcon,
  OverflowContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import { calculateDialogHeight } from 'src/utils/dialog.utils';

const ProductManagement: FC = () => {
  const { showSnackbar } = StyledSnackbar();
  const formik = useFormik({
    initialValues: productInitialValues,
    validationSchema: productValidation,
    onSubmit: (values) => handleSubmit(values),
  });

  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productAdded, setProductAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [unitList, setUnitList] = useState<string[]>([]);

  const fetchUnits = async () => {
    try {
      const enums = await fetchProductUnits();
      setUnitList(enums || []);
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const getDialogHeight = () =>
    calculateDialogHeight(
      formik,
      [
        ['name', 'displayOrder'],
        ['unit', 'unitPrice'],
      ],
      450
    );

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const addProduct = await axiosTokenInstance.post(apiUrls.ADD_PRODUCT, {
        name: values.name,
        unit: values.unit,
        unitPrice: values.unitPrice,
        displayOrder: values.displayOrder,
      });
      showSnackbar(addProduct.data.message, 'success');
      setProductAdded(!productAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    fetchUnits();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <MainLayout
      title="Product Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search products by name"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="150px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Product
        </PrimaryActionButton>
      }
    >
      <ProductGrid state={productAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title="Add New Product"
            subtitle="Fill in the details below to add a new product to the inventory."
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <TextField
                  width="300px"
                  id="name"
                  label="Name"
                  formikProps={formik}
                />
                <TextField
                  width="300px"
                  id="displayOrder"
                  label="Display Order"
                  formikProps={formik}
                />
              </Box>
              <Box display="flex" justifyContent="space-between" gap={2}>
                <UnitField
                  width="300px"
                  id="unit"
                  label="Unit"
                  formikProps={formik}
                  unitList={unitList}
                />
                <TextField
                  width="300px"
                  id="unitPrice"
                  placeholder="00.00"
                  label="Unit Price"
                  formikProps={formik}
                />
              </Box>
            </Box>
          </CustomDialog>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default ProductManagement;
